// external
import React, { FC, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Checkbox,
  Switch,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';

// internal
import { Group } from '../../models/training.models';
import { palette } from '../../theme';
import './coe-member-panel.component.css';
import { UserAvatar } from '../user-avatar/user-avatar.component';

interface COEMemberPanelProps {
  members: any[];
  selectedMembers: any[];
  onMembersChange: (members: any[]) => void;
  groups: Group[];
  selectedGroup: string | number | null;
  onGroupsChange: (groupId: string | number | null) => void;
  membersLoading?: boolean;
  groupsLoading?: boolean;
  excludedGroupIds?: (string | number)[];
  onExcludedGroupsChange?: (ids: (string | number)[]) => void;
}

const COEMemberPanel: FC<COEMemberPanelProps> = (props) => {
  const [searchQuery, setSearchQuery] = useState('');

  const isMemberSelected = (member: any) => {
    return props.selectedMembers.some((selected) => selected.id === member.id);
  };

  const isGroupSelected = (group: Group) => {
    return props.selectedGroup === group.id;
  };

  const handleMemberToggle = (member: any) => {
    const isSelected = isMemberSelected(member);
    if (isSelected) {
      props.onMembersChange(props.selectedMembers.filter((m) => m.id !== member.id));
    } else {
      props.onMembersChange([...props.selectedMembers, member]);
    }
  };

  const handleGroupToggle = (group: Group) => {
    const isSelected = isGroupSelected(group);
    props.onGroupsChange(isSelected ? null : group.id);
  };

  const isGroupExcluded = (group: Group) => {
    return (props.excludedGroupIds ?? []).includes(group.id);
  };

  const handleExcludedGroupToggle = (group: Group) => {
    if (!props.onExcludedGroupsChange) return;
    const current = props.excludedGroupIds ?? [];
    const isExcluded = current.includes(group.id);
    props.onExcludedGroupsChange(isExcluded ? current.filter((id) => id !== group.id) : [...current, group.id]);
  };

  const groupsExcludable = props.groups.filter((g) => g.id !== 'team');
  const showExcludeSection =
    props.selectedGroup === 'team' && props.excludedGroupIds != null && props.onExcludedGroupsChange != null;

  const filteredMembers = props.members.filter((m) => m.name?.toLowerCase().includes(searchQuery.toLowerCase()));

  const allFilteredSelected = filteredMembers.length > 0 && filteredMembers.every((m) => isMemberSelected(m));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      props.onMembersChange(props.selectedMembers.filter((sel) => !filteredMembers.some((m) => m.id === sel.id)));
    } else {
      const toAdd = filteredMembers.filter((m) => !isMemberSelected(m));
      props.onMembersChange([...props.selectedMembers, ...toAdd]);
    }
  };

  const columnLabelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: palette.text.secondary,
    textTransform: 'uppercase',
    marginBottom: 8,
    display: 'block',
  };

  const renderGroupRow = (group: Group, checked: boolean, onToggle: () => void, loading: boolean) => (
    <div key={group.id} className="coe-member-panel-group-row">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <GroupOutlinedIcon style={{ fontSize: 16, color: checked ? palette.primary.main : palette.text.secondary }} />
        <Typography style={{ fontSize: 13, fontWeight: 500, color: palette.text.primary }}>{group.name}</Typography>
      </div>
      {loading ? (
        <CircularProgress size={16} />
      ) : (
        <Switch
          size="small"
          checked={checked}
          onChange={onToggle}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: palette.primary.main },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: palette.primary.main,
            },
          }}
        />
      )}
    </div>
  );

  return (
    <Box className="coe-member-panel-container">
      {/* ── Groups Section ── */}
      <Box className="coe-member-panel-groups-card">
        <Typography
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#6b7084',
            marginBottom: 12,
          }}
        >
          Groups
        </Typography>

        {props.groupsLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <div className="coe-member-panel-groups-columns">
            {/* INCLUDE column */}
            <div>
              <span style={columnLabelStyle}>Include</span>
              {props.groups.length === 0 ? (
                <Typography style={{ fontSize: 13, color: palette.text.disabled }}>No groups</Typography>
              ) : (
                props.groups.map((group) =>
                  renderGroupRow(group, isGroupSelected(group), () => handleGroupToggle(group), false)
                )
              )}
            </div>

            {/* EXCLUDE column */}
            <div>
              <span style={columnLabelStyle}>Exclude</span>
              {!showExcludeSection ? (
                <Typography style={{ fontSize: 13, color: palette.text.disabled }}>
                  Select Team to exclude groups
                </Typography>
              ) : groupsExcludable.length === 0 ? (
                <Typography style={{ fontSize: 13, color: palette.text.disabled }}>No groups to exclude</Typography>
              ) : (
                groupsExcludable.map((group) =>
                  renderGroupRow(group, isGroupExcluded(group), () => handleExcludedGroupToggle(group), false)
                )
              )}
            </div>
          </div>
        )}
      </Box>

      {/* ── Assign Members Section ── */}
      <Box className="coe-member-panel-members-section">
        {/* Header row */}
        <div className="coe-member-panel-members-header">
          <Typography style={{ fontSize: 15, fontWeight: 700, color: palette.text.primary }}>Assign Members</Typography>
          <Typography style={{ fontSize: 12, color: palette.grey[400] }}>
            {props.selectedMembers.length} of {props.members.length} selected
          </Typography>
        </div>

        {/* Search + Select All */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <TextField
            size="small"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 16, color: palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 },
            }}
          />
          <Button
            variant="text"
            size="small"
            onClick={handleSelectAll}
            style={{
              textTransform: 'none',
              color: palette.primary.main,
              fontWeight: 600,
              fontSize: 12,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {allFilteredSelected ? 'Deselect All' : 'Select All'}
          </Button>
        </div>

        {/* Member list */}
        {props.membersLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List disablePadding className="coe-member-panel-members-list">
            {filteredMembers.map((member, index) => (
              <ListItem key={member.id || index} disablePadding>
                <ListItemButton
                  onClick={() => handleMemberToggle(member)}
                  className="coe-member-panel-member-row"
                  sx={{ '&:hover': { backgroundColor: 'transparent' } }}
                >
                  <Checkbox
                    checked={isMemberSelected(member)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMemberToggle(member);
                    }}
                    size="small"
                    sx={{
                      color: palette.grey[300],
                      '&.Mui-checked': { color: palette.primary.main },
                      padding: '4px 6px',
                    }}
                  />
                  <UserAvatar name={member.name || ''} size={28} fontSize="0.65rem" />
                  <Typography className="coe-member-panel-member-name">{member.name}</Typography>
                </ListItemButton>
              </ListItem>
            ))}
            {filteredMembers.length === 0 && (
              <Typography
                style={{ fontSize: 14, color: palette.text.disabled, padding: '16px 0', textAlign: 'center' }}
              >
                {searchQuery ? 'No members match your search' : 'No members available'}
              </Typography>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default COEMemberPanel;
